if (!customElements.get('flavor-size-picker')) {
  customElements.define(
    'flavor-size-picker',
    class FlavorSizePicker extends HTMLElement {
      connectedCallback() {
        const configElement = this.querySelector('script[data-flavor-size-map]');

        if (!configElement) {
          return;
        }

        const config = JSON.parse(configElement.textContent);

        this.combinations = config.combinations || [];
        this.unavailableLabel = config.unavailableLabel || '';
        this.mode = this.getAttribute('mode');
        this.flavorIndex = parseInt(this.getAttribute('flavor-position'), 10) - 1;
        this.sizeIndex = parseInt(this.getAttribute('size-position'), 10) - 1;
        this.hideUnavailableSize = this.hasAttribute('hide-unavailable-size');
        this.variantsElement = this.closest('product-variants');
        this.flavorList = this.querySelector('[data-flavor-list]');
        this.flavorLabel = this.querySelector('[data-flavor-selected-label]');
        this.sizeLabel = this.querySelector('[data-size-selected-label]');
        this.sizeCards = Array.from(this.querySelectorAll('[data-size-card]'));
        this.expandButton = this.querySelector('[data-flavor-expand]');
        this.form = document.getElementById(this.getAttribute('form-id'));
        this.isSyncing = false;

        this.addEventListener('change', this._onSelectionChanged.bind(this));

        if (this.form) {
          this.form.addEventListener('variant:changed', this._onVariantChanged.bind(this));
        }

        if (this.expandButton) {
          this.expandButton.addEventListener('click', this._expandFlavors.bind(this));
        }

        this._setupCollapse();
        this._updateSizes(this.selectedFlavor);
        this._updateLabels();
        this._updateSupplementFacts(this.selectedOptionValue);
      }

      get selectedFlavor() {
        const input = this.querySelector('[data-flavor-input]:checked');
        return input ? input.value : null;
      }

      get selectedOptionValue() {
        const input = this.variantsElement
          ? this.variantsElement.querySelector('input[name="option1"]:checked')
          : null;

        return input ? input.value : null;
      }

      get selectedSize() {
        if (this.mode === 'flavor_only') {
          return '';
        }

        const input = this.querySelector('[data-size-input]:checked');
        return input ? input.value : null;
      }

      _servingForSize(size) {
        const match = this.combinations.find((entry) => entry.size === size && entry.perServing);
        return match ? match.perServing : '';
      }

      _findCombination(flavor, size) {
        return this.combinations.find((entry) => entry.flavor === flavor && entry.size === size);
      }

      _onSelectionChanged(event) {
        if (this.isSyncing || !event.target.matches('[data-flavor-input], [data-size-input]')) {
          return;
        }

        const flavor = this.selectedFlavor;

        this._updateSizes(flavor);

        let size = this.selectedSize;

        if (!this._findCombination(flavor, size)) {
          const fallback =
            this.combinations.find((entry) => entry.flavor === flavor && entry.available) ||
            this.combinations.find((entry) => entry.flavor === flavor);

          if (!fallback) {
            return;
          }

          size = fallback.size;
          this._checkInput('[data-size-input]', size);
        }

        this._updateLabels();
        this._applyToVariantSelector(flavor, size);
      }

      _onVariantChanged(event) {
        const variant = event.detail && event.detail.variant;

        if (!variant || !variant.options) {
          return;
        }

        this._updateSupplementFacts(variant.options[0]);

        const flavorValue = variant.options[this.flavorIndex];
        const sizeValue = variant.options[this.sizeIndex];
        const combination = this.combinations.find((entry) => {
          return this.mode === 'split'
            ? entry.flavorValue === flavorValue && entry.sizeValue === sizeValue
            : entry.flavorValue === flavorValue;
        });

        if (!combination || (combination.flavor === this.selectedFlavor && combination.size === this.selectedSize)) {
          return;
        }

        this.isSyncing = true;
        this._checkInput('[data-flavor-input]', combination.flavor);
        this._updateSizes(combination.flavor);
        this._checkInput('[data-size-input]', combination.size);
        this._updateLabels();
        this.isSyncing = false;
      }

      _updateSupplementFacts(optionValue) {
        if (!optionValue) {
          return;
        }

        const images = document.querySelectorAll(
          '.small-demo.multi.bundle-variant-data img[varient_option]'
        );

        images.forEach((image) => {
          image.style.display = image.getAttribute('varient_option') === optionValue ? '' : 'none';
        });
      }

      _checkInput(selector, value) {
        const input = this.querySelector(`${selector}[value="${CSS.escape(value)}"]`);

        if (input) {
          input.checked = true;
        }
      }

      _updateSizes(flavor) {
        this.sizeCards.forEach((card) => {
          const input = card.querySelector('[data-size-input]');
          const price = card.querySelector('[data-size-price]');
          const badge = card.querySelector('[data-size-badge]');
          const combination = this._findCombination(flavor, card.dataset.sizeCard);
          const available = Boolean(combination) && combination.available;

          card.classList.toggle('is-missing', !combination);
          card.classList.toggle('is-disabled', Boolean(combination) && !combination.available);
          card.hidden = this.hideUnavailableSize && !combination;

          if (input) {
            input.disabled = !combination;
          }

          if (price) {
            price.textContent = combination ? combination.price : '';
          }

          if (badge) {
            badge.hidden = available;
          }

          const flag = card.querySelector('[data-size-flag]');

          if (flag) {
            flag.hidden = !available;
          }

          const serving = card.querySelector('[data-size-serving]');

          if (serving) {
            const text = this._servingForSize(card.dataset.sizeCard);
            serving.textContent = text;
            serving.hidden = !text;
          }
        });
      }

      _updateLabels() {
        if (this.flavorLabel) {
          this.flavorLabel.textContent = this.selectedFlavor || '';
        }

        if (this.sizeLabel) {
          this.sizeLabel.textContent = this.selectedSize || '';
        }
      }

      _applyToVariantSelector(flavor, size) {
        const combination = this._findCombination(flavor, size);

        if (!combination || !this.variantsElement) {
          return;
        }

        const targets = [];
        const flavorInput = this._findOptionInput(this.flavorIndex + 1, combination.flavorValue);

        if (flavorInput) {
          targets.push(flavorInput);
        }

        if (this.mode === 'split') {
          const sizeInput = this._findOptionInput(this.sizeIndex + 1, combination.sizeValue);

          if (sizeInput) {
            targets.push(sizeInput);
          }
        }

        if (targets.length === 0) {
          return;
        }

        targets.forEach((input) => {
          input.checked = true;
        });

        targets[targets.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
      }

      _findOptionInput(position, value) {
        return this.variantsElement.querySelector(
          `input[name="option${position}"][value="${CSS.escape(value)}"]`
        );
      }

      _setupCollapse() {
        if (!this.hasAttribute('collapse-flavors') || !this.flavorList) {
          return;
        }

        this.flavorList.classList.add('is-collapsed');

        const checked = this.querySelector('[data-flavor-input]:checked');

        if (checked && checked.parentElement.offsetParent === null) {
          this._expandFlavors();
        }
      }

      _expandFlavors() {
        this.flavorList.classList.remove('is-collapsed');

        if (this.expandButton) {
          this.expandButton.hidden = true;
        }
      }
    }
  );
}
